/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Recipes the group has actually shared, surfaced in the feed.
 *
 * ── WHY THIS IS NOT A MODAL ANY MORE ────────────────────────────────────────
 * Library sharing already existed and was good: a household opts its whole
 * library into a circle, everyone else browses it and takes copies. It lived
 * entirely behind a "Library sharing" button in the header, which meant the
 * one thing in a cooking group with lasting value — recipes you can actually
 * cook — was the one thing you had to go looking for, while ephemeral chat
 * (deleted after 30 days) occupied the whole screen.
 *
 * ── THE LINE THIS DOES NOT CROSS ────────────────────────────────────────────
 * Migration 027 is emphatic that a `suggested_title` on a message is a NAME and
 * never a recipe_id, because naming a dish is not the same as granting a read.
 * This component respects that completely: it shows only recipes whose owning
 * household has explicitly turned library sharing on for this circle — an
 * existing, deliberate grant through 029. It never tries to resolve a dish name
 * in a post into a recipe, and a post that mentions a dish says so in its own
 * card ("A dish they mentioned — not a shared recipe").
 *
 * So: chat is chat, and a shared recipe is a thing somebody chose to give you.
 * The redesign makes the second one visible without blurring it into the first.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { CirclesApi } from "../../api/endpoints.ts";
import { Icon } from "../../components/Icon.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import { shelfState } from "./shelf.ts";

export function SharedRecipeStrip({ circleId, onManage }: {
  circleId: string;
  onManage: () => void;
}) {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  // Always loaded now, rather than only when a modal opens — it is part of the
  // page, and a strip that populates after a click is a strip nobody sees.
  //
  // includeOwn=true: the strip shows the WHOLE shelf, the reader's own shares
  // included (flagged isOwn, rendered with a "Yours" chip — never an accept
  // button, which would 409). When the only shares in the circle are yours,
  // the accept-only list is legitimately empty and the strip used to announce
  // that as "nothing is shared", contradicting the sharing panel one screen
  // away. Distinct query key from the "Manage sharing" browser, which must
  // keep the accept-only list.
  const recipes = useQuery({
    queryKey: ["circle-library-shelf", circleId],
    queryFn: () => CirclesApi.library.recipes.list(circleId, true),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: (recipeId: string) => CirclesApi.library.recipes.accept(circleId, recipeId),
    onSuccess: () => {
      toast(t("circles.recipeAccepted"), "ok");
      void qc.invalidateQueries({ queryKey: ["circle-library-shelf", circleId] });
      void qc.invalidateQueries({ queryKey: ["circle-library-recipes", circleId] });
      void qc.invalidateQueries({ queryKey: ["recipes"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
    },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  const available = recipes.data?.recipes ?? [];
  const shelf = shelfState(available);

  // Nothing shared by anyone: one quiet line offering to be the first, rather
  // than an empty panel. The feed below is the main event and this must not
  // compete. (Only YOUR OWN shares existing is not this state — see shelf.ts.)
  if (!recipes.isLoading && shelf.kind === "empty") {
    return (
      <button
        type="button"
        onClick={onManage}
        className="flex w-full items-center gap-2.5 rounded-xl border border-dashed border-rule px-4 py-3 text-left text-sm text-ink-3 transition hover:border-accent/50 hover:text-ink"
      >
        <Icon name="book" className="h-4 w-4 shrink-0" />
        <span className="flex-1">No recipes shared with this group yet.</span>
        <span className="shrink-0 text-xs text-accent">Share yours</span>
      </button>
    );
  }

  if (recipes.isLoading) return null;

  return (
    <section aria-label="Recipes shared with this group">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="label-mono">
          Shared recipes · {available.length}
        </h2>
        <button className="text-xs text-accent hover:underline" onClick={onManage}>
          Manage sharing
        </button>
      </div>

      {/* Your own shares are already shared — say so instead of nudging you to
          do the thing you already did. */}
      {shelf.kind === "ownOnly" && (
        <p className="mb-2 text-xs text-ink-3">
          {t("circles.shelfOwnOnly").replace("{count}", String(shelf.ownCount))}
        </p>
      )}

      {/* Horizontal, not a grid. This is a shelf you skim past on the way to the
          feed, not a catalogue you shop from — the library browser behind
          "Manage sharing" is still there for that. */}
      <ul className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-2">
        {available.map((r) => (
          <li key={r.recipeId} className="w-56 shrink-0 snap-start">
            <div className="card flex h-full flex-col p-3">
              <div className="flex-1">
                <div className="text-sm font-medium leading-snug text-ink">{r.title}</div>
                {r.description && (
                  <p className="mt-1 line-clamp-2 text-xs text-ink-3">{r.description}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-ink-3">
                  {r.totalMinutes ? <span>{r.totalMinutes} min</span> : null}
                  <span>· {r.servingLabel}</span>
                  {r.cuisine && <span>· {r.cuisine}</span>}
                </div>
              </div>

              {r.isOwn ? (
                // Your own copy is directly readable — it is yours — so the row
                // taps straight through to it. Never an accept button.
                <button
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
                  onClick={() => navigate(`/recipes/${r.recipeId}`)}
                >
                  <Icon name="check" className="h-3.5 w-3.5" />
                  {t("circles.shelfYours")}
                </button>
              ) : r.alreadyAccepted ? (
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs text-ok">
                  <Icon name="check" className="h-3.5 w-3.5" />
                  In your library
                </span>
              ) : (
                <button
                  className="btn-ghost mt-3 !py-1.5 text-xs"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate(r.recipeId)}
                >
                  <Icon name="plus" className="h-3.5 w-3.5" />
                  Add to my library
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
